import {
  type ComponentProps,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { sumBy } from 'lodash';
import {
  type BaseRange,
  type Descendant,
  Editor,
  Range as SlateRange,
  Text as SlateText,
  Transforms,
} from 'slate';
import { ReactEditor, type RenderLeafProps, useSlateSelector, useSlateStatic } from 'slate-react';

import { IconCopy_24 } from 'src/assets/icons/24x24/IconCopy_24';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTooltip } from 'src/components/CustomTooltip';
import {
  CustomEditable,
  FixedToolbar,
  TextLeaf,
  toggleMark,
  // @ts-expect-error - no TypeScript definitions yet
} from 'src/components/RichSlate';
import {
  SAVED_DOCUMENT_ITEM_TYPE,
  SAVED_DOCUMENT_RATING,
  type SavedDocumentAnchorUpdate,
  type SavedDocumentThread,
} from 'src/data/savedDocument';
import { useFreshRef, useOnClickOutside } from 'src/lib/hooks';
import { SLATE_EDITOR_ACTION, SLATE_TEXT_FORMATS } from 'src/lib/slate';
import type { CallbackN, VoidFunction } from 'src/lib/types';

import { CommentComposer } from './components/CommentComposer';
import { FloatingPanel } from './components/FloatingPanel';
import { RatingCard } from './components/RatingCard';
import { ReviewBubble } from './components/ReviewBubble';
import { SelectionToolbar } from './components/SelectionToolbar';
import { ThreadCard } from './components/ThreadCard';
import { getAnchorFromRange, getDocumentText } from './lib/anchors';
import { findOpenBubble, placeBubbles } from './lib/bubbles';
import { BUBBLE_HEIGHT, SAVED_DOCUMENT_PANEL_GAP } from './lib/const';
import {
  getRangeKey,
  getRatingsTarget,
  getThreadTarget,
  getTrackedItems,
  type RatingGroup,
} from './lib/review';
import { getOffsetWithin } from './lib/utils';
import { useAnchorTracking } from './services/useAnchorTracking';
import { useDocumentCounters } from './services/useDocumentCounters';
import { useDocumentLayout } from './services/useDocumentLayout';
import { useSavedDocumentPolling } from './services/useSavedDocumentPolling';
import { useTextSelection } from './services/useTextSelection';
import styles from './SavedDocumentView.module.scss';
import { SAVED_DOCUMENT_PANEL_TYPE, useSavedDocumentViewActions } from './store';
import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';

const PLACEHOLDER = 'Write your copy here.';
const FORMATTING_ACTIONS = [
  [
    SLATE_EDITOR_ACTION.toggleBold,
    SLATE_EDITOR_ACTION.toggleItalic,
    SLATE_EDITOR_ACTION.toggleUnderline,
    SLATE_EDITOR_ACTION.toggleStrikethrough,
  ],
  [SLATE_EDITOR_ACTION.toggleH1, SLATE_EDITOR_ACTION.toggleH2, SLATE_EDITOR_ACTION.toggleH3],
  [SLATE_EDITOR_ACTION.toggleBulletedList, SLATE_EDITOR_ACTION.toggleNumberedList],
  [SLATE_EDITOR_ACTION.insertLink, SLATE_EDITOR_ACTION.toggleBlockQuote],
  [
    SLATE_EDITOR_ACTION.alignTextLeft,
    SLATE_EDITOR_ACTION.alignTextCenter,
    SLATE_EDITOR_ACTION.alignTextRight,
  ],
  [SLATE_EDITOR_ACTION.undo, SLATE_EDITOR_ACTION.redo],
];
const MARK_HOTKEYS: Record<string, string> = {
  b: SLATE_TEXT_FORMATS.BOLD,
  i: SLATE_TEXT_FORMATS.ITALIC,
  u: SLATE_TEXT_FORMATS.UNDERLINE,
};

// Floating UI marks itself so a click inside it does not close the open panel.
const FLOATING_ATTRIBUTE = 'data-review-floating';
const FLOATING_PROPS = { [FLOATING_ATTRIBUTE]: '' };
// A panel's menus and dialogs render in portals outside the document.
const PORTAL_LAYER_SELECTOR = '[role="presentation"], [role="dialog"], [role="tooltip"]';
export interface SavedDocumentContentChange {
  value: Descendant[];
  // Where the comments and ratings whose text moved are now, to be saved with the text.
  anchors: SavedDocumentAnchorUpdate[];
  // Called once the text and the anchors are saved.
  onSaved: VoidFunction;
}

export interface SavedDocumentViewProps {
  className?: string;
  readOnly: boolean;
  isVisible?: boolean;
  detailsOpen?: boolean;
  toolbarActions?: ReactNode;
  // The toolbar stays at the top of the scrolled area while the document scrolls under it.
  stickyToolbar?: boolean;
  // The height of the host's bar fixed over the top of the scrolled area.
  coveredTop?: number;
  editableProps?: Partial<ComponentProps<'div'>>;
  renderScores?: (parentElement: HTMLElement) => ReactNode;
  onContentChange?: CallbackN<[SavedDocumentContentChange]>;
  onCopyClick?: VoidFunction;
  onPanelOpen?: VoidFunction;
}

interface ReviewDecoration extends BaseRange {
  reviewTarget?: string;
  reviewActive?: boolean;
  reviewDraft?: boolean;
}

export const SavedDocumentView = ({
  className,
  readOnly,
  isVisible = true,
  detailsOpen = false,
  toolbarActions,
  stickyToolbar = false,
  coveredTop = 0,
  editableProps,
  renderScores,
  onContentChange,
  onCopyClick,
  onPanelOpen,
}: SavedDocumentViewProps) => {
  const editor = useSlateStatic() as ReactEditor;
  // A new array on every change to the content, and only then.
  const value = useSlateSelector((current) => current.children);
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

  const { threads, textRatings, draft, openPanel, creatingThread, replyingThreadId } =
    useViewState();
  const savedDocumentViewActions = useSavedDocumentViewActions();
  const counters = useDocumentCounters(value);
  useSavedDocumentPolling(isVisible);

  const { resolveItem, collectAnchorUpdates, releaseMissing } = useAnchorTracking({
    editor,
    enabled: !readOnly,
  });
  const { selection, clearSelection } = useTextSelection({ editor, rootElement, readOnly });

  // `value` is what the editor's text is read from, though the read goes through `editor`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const documentText = useMemo(() => getDocumentText(editor), [editor, value]);
  const trackedItems = useMemo(() => getTrackedItems(threads, textRatings), [threads, textRatings]);

  useEffect(() => releaseMissing(trackedItems), [trackedItems, releaseMissing]);

  // The anchors are read when the text changes, after Slate has moved its range refs with it.
  const contentChangeRef = useFreshRef(() => {
    onContentChange?.({
      value,
      anchors: collectAnchorUpdates(documentText, trackedItems),
      onSaved: () => savedDocumentViewActions.fetchSavedDocument(),
    });
  });
  const initialValueRef = useRef(value);
  useEffect(() => {
    if (!readOnly && value !== initialValueRef.current) {
      contentChangeRef.current?.();
    }
  }, [readOnly, value, contentChangeRef]);

  // Threads and ratings whose text is gone are not shown; they stay in the data.
  const visibleThreads = useMemo(
    () =>
      threads.flatMap((thread) => {
        const resolved = resolveItem(documentText, {
          type: SAVED_DOCUMENT_ITEM_TYPE.thread,
          id: thread.id,
          anchor: thread.anchor,
        });
        return resolved ? [{ thread, resolved }] : [];
      }),
    [threads, documentText, resolveItem]
  );

  const ratingGroups = useMemo(() => {
    const groups = new Map<string, RatingGroup>();
    textRatings.forEach((rating) => {
      const resolved = resolveItem(documentText, {
        type: SAVED_DOCUMENT_ITEM_TYPE.textRating,
        id: rating.id,
        anchor: rating.anchor,
      });
      if (!resolved) {
        return;
      }
      const key = getRangeKey(resolved);
      const quote = documentText.text.slice(resolved.start, resolved.end);
      const group = groups.get(key) ?? { key, quote, ratings: [], resolved };
      group.ratings.push(rating);
      groups.set(key, group);
    });
    return [...groups.values()];
  }, [textRatings, documentText, resolveItem]);

  const selectionAnchor = useMemo(
    () => (selection ? getAnchorFromRange(editor, documentText, selection.range) : null),
    [selection, editor, documentText]
  );

  const highlights = useMemo(
    () => [
      ...visibleThreads.map(({ thread, resolved }) => ({
        target: getThreadTarget(thread.id),
        range: resolved.range,
      })),
      ...ratingGroups.map(({ key, resolved }) => ({
        target: getRatingsTarget(key),
        range: resolved.range,
      })),
    ],
    [visibleThreads, ratingGroups]
  );

  const highlightEnds = useDocumentLayout({ editor, rootElement, highlights, isVisible });

  const bubbles = useMemo(
    () =>
      placeBubbles(
        highlights.flatMap(({ target }) => {
          const centre = highlightEnds[target];
          return centre === undefined ? [] : [{ target, top: centre - BUBBLE_HEIGHT / 2 }];
        })
      ).map((cluster) => {
        const bubbleThreads = visibleThreads
          .filter(({ thread }) => cluster.targets.includes(getThreadTarget(thread.id)))
          .map(({ thread }) => thread);
        const bubbleGroups = ratingGroups.filter(({ key }) =>
          cluster.targets.includes(getRatingsTarget(key))
        );
        const ratings = bubbleGroups.flatMap((group) => group.ratings);
        return {
          ...cluster,
          threads: bubbleThreads,
          ratingGroups: bubbleGroups,
          commentCount: sumBy(bubbleThreads, (thread) => thread.comments.length),
          thumbsUpCount: ratings.filter(
            ({ value: rating }) => rating === SAVED_DOCUMENT_RATING.thumbsUp
          ).length,
          thumbsDownCount: ratings.filter(
            ({ value: rating }) => rating === SAVED_DOCUMENT_RATING.thumbsDown
          ).length,
        };
      }),
    [highlights, highlightEnds, visibleThreads, ratingGroups]
  );

  const activeBubble =
    openPanel?.type === SAVED_DOCUMENT_PANEL_TYPE.bubble
      ? findOpenBubble(bubbles, openPanel.targets)
      : undefined;

  const activeTarget = useMemo(() => {
    if (openPanel?.type === SAVED_DOCUMENT_PANEL_TYPE.thread) {
      return getThreadTarget(openPanel.key);
    }
    if (openPanel?.type === SAVED_DOCUMENT_PANEL_TYPE.ratings) {
      return getRatingsTarget(openPanel.key);
    }
    return null;
  }, [openPanel]);

  const panel = useMemo(() => {
    if (!openPanel) {
      return null;
    }
    if (openPanel.type === SAVED_DOCUMENT_PANEL_TYPE.bubble) {
      return activeBubble
        ? {
            anchor: { top: activeBubble.top, bottom: activeBubble.top + BUBBLE_HEIGHT },
            threads: activeBubble.threads,
            ratingGroups: activeBubble.ratingGroups,
          }
        : null;
    }
    if (openPanel.type === SAVED_DOCUMENT_PANEL_TYPE.thread) {
      const item = visibleThreads.find(({ thread }) => thread.id === openPanel.key);
      return { anchor: openPanel.anchor, threads: item ? [item.thread] : [], ratingGroups: [] };
    }
    const group = ratingGroups.find(({ key }) => key === openPanel.key);
    return { anchor: openPanel.anchor, threads: [], ratingGroups: group ? [group] : [] };
  }, [openPanel, activeBubble, visibleThreads, ratingGroups]);
  const panelItemCount = panel ? panel.threads.length + panel.ratingGroups.length : 0;
  const [toolbarElement, setToolbarElement] = useState<HTMLDivElement | null>(null);
  const getCoveredTop = useCallback(
    () => coveredTop + (stickyToolbar ? (toolbarElement?.offsetHeight ?? 0) : 0),
    [coveredTop, stickyToolbar, toolbarElement]
  );

  // The details panel covers the margin, so only one of the two is open at a time. A comment with
  // text is hidden under it instead, and comes back when it closes.
  useEffect(() => {
    if (detailsOpen) {
      savedDocumentViewActions.closePanelsUnlessDraftHasText();
    }
  }, [detailsOpen, savedDocumentViewActions]);

  // The panel of a comment being written follows its text when edits move it. Placing the panel at
  // the same spot again would render the editor while the comment field takes the focus.
  useLayoutEffect(() => {
    const range = draft?.rangeRef.current;
    if (!draft || !range || SlateRange.isCollapsed(range) || !rootElement) {
      return;
    }
    try {
      const position = getOffsetWithin(
        rootElement,
        ReactEditor.toDOMRange(editor, range).getBoundingClientRect()
      );
      if (position.top !== draft.position.top || position.bottom !== draft.position.bottom) {
        savedDocumentViewActions.setDraftPosition(position);
      }
    } catch {
      // The range is not rendered yet. The next change places the panel.
    }
  }, [value, draft, rootElement, editor, savedDocumentViewActions]);

  useEffect(
    () => () => {
      savedDocumentViewActions.getDraft()?.rangeRef.unref();
    },
    [savedDocumentViewActions]
  );

  const hasOpenPanel = panelItemCount > 0 || draft !== null;
  useEffect(() => {
    if (hasOpenPanel) {
      onPanelOpen?.();
    }
  }, [hasOpenPanel, onPanelOpen]);

  const {
    currentUser,
    currentUserId,
    handleStartComment,
    handleRateSelection,
    handleSubmitComment,
    handleReply,
    handleResolveThread,
    handleEditComment,
    handleDeleteComment,
    handleRemoveTextRating,
  } = useHandlers({
    editor,
    documentText,
    ratingGroups,
    selection,
    selectionAnchor,
    clearSelection,
  });

  const decorate = useCallback(
    ([node, path]: [unknown, number[]]): ReviewDecoration[] => {
      if (!SlateText.isText(node)) {
        return [];
      }
      const nodeRange = Editor.range(editor, path);
      const ranges: ReviewDecoration[] = highlights.flatMap(({ target, range }) => {
        const intersection = SlateRange.intersection(range, nodeRange);
        if (!intersection || SlateRange.isCollapsed(intersection)) {
          return [];
        }
        return [
          {
            ...intersection,
            reviewTarget: target,
            reviewActive:
              target === activeTarget || Boolean(activeBubble?.targets.includes(target)),
          },
        ];
      });

      const draftRange = draft?.rangeRef.current;
      const draftIntersection = draftRange && SlateRange.intersection(draftRange, nodeRange);
      if (draftIntersection && !SlateRange.isCollapsed(draftIntersection)) {
        ranges.push({ ...draftIntersection, reviewDraft: true });
      }
      return ranges;
    },
    [editor, highlights, draft, activeTarget, activeBubble]
  );

  const openFromHighlight = useCallback(
    (target: string, event: MouseEvent<HTMLElement>) => {
      // A click that ends a drag-selection is a selection, not a request to open the panel.
      if (!rootElement || !window.getSelection()?.isCollapsed) {
        return;
      }
      const { top, bottom } = getOffsetWithin(
        rootElement,
        event.currentTarget.getBoundingClientRect()
      );
      const anchor = { top, bottom };
      const separator = target.indexOf(':');
      const key = target.slice(separator + 1);
      savedDocumentViewActions.openPanelUnlessDraftHasText(
        target === getThreadTarget(key)
          ? { type: SAVED_DOCUMENT_PANEL_TYPE.thread, key, anchor }
          : { type: SAVED_DOCUMENT_PANEL_TYPE.ratings, key, anchor }
      );
    },
    [rootElement, savedDocumentViewActions]
  );

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => {
      const leaf = props.leaf as RenderLeafProps['leaf'] &
        Omit<ReviewDecoration, 'anchor' | 'focus'>;
      const target = leaf.reviewTarget;

      return (
        <TextLeaf
          {...props}
          className={cx({
            [styles.highlight]: Boolean(target),
            [styles.highlightActive]: leaf.reviewActive,
            [styles.draft]: leaf.reviewDraft,
          })}
          {...(target && {
            'data-review-target': target,
            'data-testid': 'savedDocumentHighlight',
            onClick: (event: MouseEvent<HTMLElement>) => openFromHighlight(target, event),
          })}
        />
      );
    },
    [openFromHighlight]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const mark = MARK_HOTKEYS[event.key];
    if (!readOnly && mark && (event.metaKey || event.ctrlKey)) {
      toggleMark(editor, mark);
    }
  };

  const rootRef = useMemo(() => ({ current: rootElement }), [rootElement]);
  const handleOutsideMouseDown = useCallback(
    (event: globalThis.MouseEvent | TouchEvent) => {
      if (!(event.target as Element).closest?.(PORTAL_LAYER_SELECTOR)) {
        savedDocumentViewActions.closePanelsUnlessDraftHasText();
      }
    },
    [savedDocumentViewActions]
  );
  useOnClickOutside(rootRef, handleOutsideMouseDown);

  const handleRootMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    // React bubbles events from portals (a comment's menu) here, though their DOM is elsewhere.
    if (!event.currentTarget.contains(target) || target.closest(`[${FLOATING_ATTRIBUTE}]`)) {
      return;
    }
    savedDocumentViewActions.closePanelsUnlessDraftHasText();
    selectClickedPoint(event);
  };

  // Slate reads a click's caret on the next `selectionchange`. Every render writes Slate's selection
  // to the page. A render in between puts the old selection back over the caret. Typing then
  // replaces that old selection. Focusing the editor renders it. A click that opens or closes a
  // panel renders it too. The click point is selected first to prevent this. A right click keeps
  // the selection for the context menu. A double click selects a word.
  const selectClickedPoint = (event: MouseEvent<HTMLDivElement>) => {
    if (readOnly || event.button !== 0 || event.shiftKey || event.detail > 1) {
      return;
    }
    try {
      if (ReactEditor.hasDOMNode(editor, event.target as Node, { editable: true })) {
        Transforms.select(editor, ReactEditor.findEventRange(editor, event));
      }
    } catch {
      // The click is not on the text, e.g. on an image. The editor handles it as before.
    }
  };

  const renderThread = (thread: SavedDocumentThread, active: boolean) => (
    <ThreadCard
      key={thread.id}
      thread={thread}
      active={active}
      replying={replyingThreadId === thread.id}
      onFocus={() => {
        if (!active && panel) {
          savedDocumentViewActions.setOpenPanel({
            type: SAVED_DOCUMENT_PANEL_TYPE.thread,
            key: thread.id,
            anchor: panel.anchor,
          });
        }
      }}
      onReply={(text) => handleReply(thread.id, text)}
      onResolve={() => handleResolveThread(thread.id)}
      onEditComment={handleEditComment}
      onDeleteComment={(comment) => handleDeleteComment(thread, comment)}
    />
  );

  return (
    <div
      className={cx(styles.root, className)}
      onKeyDown={handleKeyDown}
      data-testid="SavedDocumentView"
    >
      <div
        className={cx(styles.toolbarWrapper, { [styles.stickyToolbar]: stickyToolbar })}
        ref={setToolbarElement}
      >
        <div className={styles.toolbar}>
          {readOnly ? (
            <div className={styles.formatting} />
          ) : (
            <FixedToolbar
              className={styles.formatting}
              groupedActions={FORMATTING_ACTIONS}
            />
          )}

          <div className={styles.counters}>
            <span className={styles.counter}>{counters.characters} characters</span>
            <span className={styles.counter}>{counters.words} words</span>
            {onCopyClick && (
              <CustomTooltip title="Copy to clipboard">
                <CustomIconButton
                  className={styles.copyButton}
                  size="small"
                  onClick={onCopyClick}
                >
                  <IconCopy_24 />
                </CustomIconButton>
              </CustomTooltip>
            )}
          </div>

          {toolbarActions && <div className={styles.toolbarActions}>{toolbarActions}</div>}
        </div>
      </div>

      <div
        className={styles.document}
        ref={setRootElement}
        onMouseDown={handleRootMouseDown}
      >
        <div
          className={styles.content}
          ref={setContentElement}
        >
          <CustomEditable
            {...editableProps}
            className={cx(styles.editable, editableProps?.className)}
            placeholder={readOnly ? undefined : PLACEHOLDER}
            readOnly={readOnly}
            decorate={decorate}
            renderLeaf={renderLeaf}
          />
          {contentElement && renderScores?.(contentElement)}
        </div>

        {bubbles.map((bubble) => (
          <div
            key={bubble.key}
            {...FLOATING_PROPS}
          >
            <ReviewBubble
              className={styles.bubble}
              style={{ top: bubble.top }}
              commentCount={bubble.commentCount}
              thumbsUpCount={bubble.thumbsUpCount}
              thumbsDownCount={bubble.thumbsDownCount}
              active={activeBubble?.key === bubble.key}
              onClick={() =>
                savedDocumentViewActions.openPanelUnlessDraftHasText({
                  type: SAVED_DOCUMENT_PANEL_TYPE.bubble,
                  targets: bubble.targets,
                })
              }
            />
          </div>
        ))}

        {selection && selectionAnchor && !draft && (
          <div {...FLOATING_PROPS}>
            <SelectionToolbar
              style={{
                top: selection.end.top - SAVED_DOCUMENT_PANEL_GAP,
                left: selection.end.left,
              }}
              onComment={handleStartComment}
              onThumbsUp={() => handleRateSelection(SAVED_DOCUMENT_RATING.thumbsUp)}
              onThumbsDown={() => handleRateSelection(SAVED_DOCUMENT_RATING.thumbsDown)}
            />
          </div>
        )}

        {draft && currentUser && (
          <FloatingPanel
            className={cx(styles.rail, styles.composer)}
            rootElement={rootElement}
            getCoveredTop={getCoveredTop}
            anchor={draft.position}
            hidden={detailsOpen}
            {...FLOATING_PROPS}
          >
            <CommentComposer
              author={{ name: currentUser.name ?? currentUser.email }}
              pending={creatingThread}
              onSubmit={handleSubmitComment}
              onHasTextChange={savedDocumentViewActions.setDraftHasText}
              onCancel={() => savedDocumentViewActions.closePanels()}
            />
          </FloatingPanel>
        )}

        {panel && panelItemCount > 0 && (
          <FloatingPanel
            className={cx(styles.rail, styles.panel)}
            rootElement={rootElement}
            getCoveredTop={getCoveredTop}
            anchor={panel.anchor}
            {...FLOATING_PROPS}
          >
            {panel.threads.map((thread) =>
              renderThread(
                thread,
                panelItemCount === 1 || activeTarget === getThreadTarget(thread.id)
              )
            )}
            {panel.ratingGroups.map((group) => (
              <RatingCard
                key={group.key}
                quote={group.quote}
                ratings={group.ratings}
                currentUserId={currentUserId}
                onRemove={handleRemoveTextRating}
              />
            ))}
          </FloatingPanel>
        )}
      </div>
    </div>
  );
};
